import { ReactNode, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { useTranslation } from "react-i18next";
import { LinearProgress } from '@mui/material';

import { validateEmail } from './SignIn';
import { requestResetPassword } from '../../api';

interface ForgotPasswordProps {
  onCancel?: () => void;
  onRequested?: () => void;
}

interface ResetPasswordValues {
  email: string;
}

const ForgotPassword = ({ onCancel, onRequested }: ForgotPasswordProps) => {
  const { t } = useTranslation("translation");

  const [error, setError] = useState<string | ReactNode | undefined | null>(undefined);
  const [message, setMessage] = useState<string | ReactNode | undefined | null>(undefined);

  const onResetPassword = async (values: ResetPasswordValues) => {
    const onClickOkay = () => {
      setMessage(null);
      if (onRequested)
        onRequested();
    }

    const response = await requestResetPassword(values.email);

    if (!response)
      setError(t("internet-disconnected"));
    else if (response.status !== 200)
      setError(t("invalid-email"));
    else
      setMessage(<>
        <p>{t('password-reset-requested')}</p>
        <p>{t('check-your-email')}</p>
        <CheckCircleOutlineIcon className='success' />
        <Button variant='contained' onClick={onClickOkay}>OK</Button>
      </>);
  }

  return <div className="auth-component">
    <h1>{t("forgotten-password")}</h1>
    <h2>{t("forgotten-password-desc")}</h2>

    <Formik
      initialValues={{
        email: ''
      }}
      validate={validateEmail}
      onSubmit={async (values, { setSubmitting }) => {
        setError(undefined);
        setSubmitting(true);
        await onResetPassword(values);
        setSubmitting(false);
      }}
    >
      {({ submitForm, isSubmitting }) => (
        <Form>
          <Box margin={1}>
            <Field
              component={TextField}
              name="email"
              type="email"
              label="Email"
              helperText={t("enter-email")}
            />
          </Box>

          <Box margin={1}>
            <Button
              className="signin-button"
              variant="contained"
              disabled={isSubmitting}
              onClick={submitForm}>
              {t("reset-password")}
            </Button>
          </Box>

          <LinearProgress style={{ marginTop: "1em", opacity: isSubmitting ? 1 : 0 }} />
        </Form>
      )}
    </Formik>

    {message && <div className="overlay-message">{message}</div>}

    {error &&
      <Box marginBottom={3}>
        <p className="error-message">{error}</p>
      </Box>}

    <Box margin={1}>
      <div className="link-button" onClick={() => onCancel ? onCancel() : ""}>{t("cancel")}</div>
    </Box>
  </div>;
}

export default ForgotPassword;
